/**
 * Material variant sets — shared editor utilities.
 *
 * A "variant set" is a color Properties declaration carrying named candidate
 * values (`variants: [{name, value}]`), bound to a material slot's
 * `baseColorFactor`. Editors render the variants as a swatch row; clicking a
 * swatch writes its VALUE through the normal color path. Variant NAMES are
 * display-only — nothing references a variant by name.
 *
 * The field is pure authoring metadata: runtime resolution ignores `variants`.
 * These helpers only read/mutate the authored declaration; the actual color
 * write on a swatch click goes through `setMaterialColorForState` (the same
 * call the color picker makes), so state routing / undo / preview-regen are
 * inherited.
 *
 * Like `renderer-bindings`, every mutation takes SceneObject refs (never name
 * strings), records undo via the shared UndoManager unless `record: false`, and
 * announces the change via `dispatchBehaviorChanged` AFTER the commit + push.
 */

import type { SceneObject } from '../core/SceneObject';
import type { ValidationError } from '../scenedata/behaviordata';
import type { ColorDeclaration, PropertyDeclaration, TimeTransition } from '../scenedata/behaviors/propertiesdata';
import { WHOLE_REF_RE, wholeRefName } from '../behaviors/tracebehavior';
import { Renderer } from '../behaviors/renderer';
import { Properties } from '../behaviors/properties';
import type { UndoCommand } from './undo/UndoCommand';
import { getUndoManager } from './undo/UndoManager';
import { BehaviorCommitCommand } from './undo/BehaviorCommitCommand';
import { CompositeCommand } from './undo/CompositeCommand';
import { dispatchBehaviorChanged } from './undo/editor-events';
import { bindMaterialColorToProperty, bindRendererProperty } from './renderer-bindings';
import { findPropertyProvider } from './property-provider';

/**
 * Names reserved for the future intrinsic-property namespace; the sanitizer
 * must never produce one (docs-internal/VARIANTS_DESIGN.md, intrinsic properties).
 */
export const RESERVED_PROPERTY_NAMES = [
    'position', 'rotation', 'scale', 'transform', 'active', 'visible',
] as const;

/** Strip an authored color to 6-digit hex (`#rrggbbaa` → `#rrggbb`). */
function toSixDigitHex(hex: string): string {
    return /^#[0-9A-Fa-f]{8}$/.test(hex) ? hex.slice(0, 7) : hex;
}

/**
 * House default transition seeded onto a new variant set (matches the add-state
 * default). Animated variant switching relies on this being present on the
 * declaration so `setValue`/graph taps/play mode cross-fade rather than snap.
 */
const DEFAULT_VARIANT_TRANSITION: TimeTransition = { duration: 0.3, easing: 'easeOut' };

/**
 * Identifier-ize a display name for use as a property name. The binding grammar
 * (`WHOLE_REF_RE`) accepts `[a-zA-Z_]\w*`: strip invalid chars, prefix `_` if
 * the first surviving char is invalid, fall back to `_` for an empty result,
 * append `_color` if the result is reserved, then dedupe against `existing`
 * with numeric suffixes (`Body`, `Body2`, `Body3`…).
 */
export function sanitizePropertyName(display: string, existing: ReadonlySet<string>): string {
    let base = display.replace(/[^a-zA-Z0-9_]/g, '');
    if (base.length === 0 || !/^[a-zA-Z_]/.test(base)) base = `_${base}`;
    if ((RESERVED_PROPERTY_NAMES as readonly string[]).includes(base)) base = `${base}_color`;

    if (!existing.has(base)) return base;
    let i = 2;
    while (existing.has(`${base}${i}`)) i++;
    return `${base}${i}`;
}

export interface MaterialVariantSet {
    propertyName: string;
    /** Object whose Properties behavior declares the property (may be an ancestor). */
    provider: SceneObject;
    declaration: ColorDeclaration;
    /** Normalized style shape — values keyed by property name (one key in MVP). */
    variants: { name: string; values: Record<string, string> }[];
    /** The property's currently-resolving value (for swatch ring highlighting). */
    currentValue: string | undefined;
    /** The declaration's base (default) value, 6-digit hex — the position-0 swatch. */
    defaultValue: string;
    /**
     * Index into `variants` whose value matches `currentValue` (alpha-insensitive),
     * or -1 when the current value is the base default or an off-list color.
     * Editors recolor THIS variant in place when the color picker commits.
     */
    activeIndex: number;
}

/**
 * Read the variant set bound to `materials[slot].baseColorFactor`, or null if
 * the slot is unbound / bound to a non-color or variant-less declaration.
 * Synchronous, read-only, no side effects.
 */
export function getMaterialVariantSet(sceneObject: SceneObject, slot: number): MaterialVariantSet | null {
    const renderer = sceneObject.getBehavior(Renderer);
    if (!renderer) return null;

    const authoredColor = (renderer.getData().materials ?? [])
        .find(m => (m.targetMaterialIndex ?? 0) === slot)?.baseColorFactor;
    if (typeof authoredColor !== 'string') return null;
    const refMatch = authoredColor.match(WHOLE_REF_RE);
    if (!refMatch) return null;

    const propertyName = wholeRefName(refMatch);
    const provider = findPropertyProvider(sceneObject, propertyName);
    if (!provider) return null;

    const decl = (provider.properties.getData().properties ?? [])
        .find(d => d.name === propertyName);
    // An emptied `variants: []` is NOT a variant set — removing the last option
    // reverts the declaration to a plain bound color (row disappears, add-state
    // pins it again). Keep this `.length > 0` in sync with the pin-skip in
    // statesActions and `hasVariants` in renderer-bindings.
    if (!decl || decl.type !== 'color' || !decl.variants || decl.variants.length === 0) return null;

    const resolved = provider.properties.getValue(propertyName);
    const currentValue = typeof resolved === 'string' ? resolved : undefined;
    const activeIndex = currentValue
        ? decl.variants.findIndex(v => sameHex(v.value, currentValue))
        : -1;
    return {
        propertyName,
        provider: provider.owner,
        declaration: decl,
        variants: decl.variants.map(v => ({ name: v.name, values: { [propertyName]: v.value } })),
        currentValue,
        defaultValue: toSixDigitHex(decl.value),
        activeIndex,
    };
}

/** Case- and alpha-insensitive hex equality. */
function sameHex(a: string, b: string): boolean {
    return toSixDigitHex(a).toLowerCase() === toSixDigitHex(b).toLowerCase();
}

export interface CreateVariantSetResult {
    errors: ValidationError[];
    propertyName: string | null;
    command: UndoCommand | null;
}

/**
 * The "Variants" action — creates (or adopts) a variant set on a material slot,
 * as ONE undo entry covering whichever of these apply:
 *
 * - slot UNBOUND → bind it via `bindMaterialColorToProperty({freeze: false})`,
 *   naming the property from `materialName` (invariant 6: no freeze — variants
 *   compose with states).
 * - slot BOUND → ADOPT the existing declaration; never create a second one.
 * - then, if the declaration has no `variants` yet, seed exactly one
 *   (`Variant 1` = the current resolved color, alpha stripped to `#rrggbb`).
 *
 * Already-bound + already-has-variants → no-op success, EXCEPT it seeds the
 * house-default `transition` when the declaration lacks one (migrating a set
 * authored before P2). A set that already carries a transition is a true no-op.
 */
export async function createMaterialVariantSet(
    sceneObject: SceneObject,
    options: { slot?: number; materialName?: string } & { record?: boolean } = {},
): Promise<CreateVariantSetResult> {
    const renderer = sceneObject.getBehavior(Renderer);
    if (!renderer) {
        return {
            errors: [{ message: `No Renderer behavior on "${sceneObject.name}"`, code: 'NO_RENDERER' }],
            propertyName: null, command: null,
        };
    }

    const slot = options.slot ?? 0;
    const record = options.record !== false;
    const commands: UndoCommand[] = [];
    const rollback = async (): Promise<void> => {
        for (let i = commands.length - 1; i >= 0; i--) await commands[i].undo();
    };

    // 1. Resolve — or lazily create — the bound color property for this slot.
    const authoredColor = (renderer.getData().materials ?? [])
        .find(m => (m.targetMaterialIndex ?? 0) === slot)?.baseColorFactor;
    const refMatch = typeof authoredColor === 'string' ? authoredColor.match(WHOLE_REF_RE) : null;

    let propertyName: string;
    if (refMatch) {
        // Bound → adopt the existing declaration; do NOT create a second one.
        propertyName = wholeRefName(refMatch);
    } else {
        const materialName = options.materialName ?? 'color';
        // REUSE, don't orphan: an earlier variant set on this material may have
        // left a color declaration behind (e.g. "Restore Default" cleared the
        // Renderer binding but not the declaration). Re-bind THAT declaration
        // rather than minting Body2/Body3 and stranding the first — matching by
        // label (the pretty material name) or its un-deduped sanitized name.
        const reusable = findReusableColorDeclaration(sceneObject, materialName);
        if (reusable) {
            // Bind on THIS object's Renderer (the slot lives here); the reused
            // declaration may be provided by an ancestor, but the $ref resolves
            // through the provider walk regardless.
            const bind = await bindRendererProperty(
                sceneObject, reusable.name, { binding: 'materialColor', slot }, { record: false },
            );
            if (bind.errors.length > 0 || !bind.command) {
                await rollback();
                return { errors: bind.errors, propertyName: null, command: null };
            }
            commands.push(bind.command);
            propertyName = reusable.name;
        } else {
            // No reusable declaration → bind a fresh one (no freeze). The bind
            // seeds the declaration's value from the slot's current base color,
            // which we read back for the variant seed below.
            const bind = await bindMaterialColorToProperty(sceneObject, {
                slot,
                propertyName: sanitizePropertyName(materialName, collectDeclaredNames(sceneObject)),
                label: materialName,
                freeze: false,
                record: false,
            });
            if (bind.errors.length > 0 || !bind.command || !bind.propertyName) {
                await rollback();
                return { errors: bind.errors, propertyName: null, command: null };
            }
            commands.push(bind.command);
            propertyName = bind.propertyName;
        }
    }

    // 2. Seed one variant if the declaration has none yet.
    const provider = findPropertyProvider(sceneObject, propertyName);
    if (!provider) {
        await rollback();
        return {
            errors: [{ message: `Bound property "${propertyName}" has no providing Properties behavior`, code: 'NO_PROVIDER' }],
            propertyName: null, command: null,
        };
    }
    const declBefore = (provider.properties.getData().properties ?? [])
        .find(d => d.name === propertyName);
    if (!declBefore || declBefore.type !== 'color') {
        await rollback();
        return {
            errors: [{ message: `Property "${propertyName}" is not a color declaration`, code: 'NOT_COLOR' }],
            propertyName: null, command: null,
        };
    }

    // Seed a house-default transition when the declaration has none (create OR
    // adopt) — never overwrite an existing one. Folded into the variant seed's
    // commit when we also seed variants; otherwise its own commit.
    const seedTransition = declBefore.transition === undefined
        ? DEFAULT_VARIANT_TRANSITION
        : undefined;

    if (!declBefore.variants || declBefore.variants.length === 0) {
        const resolved = provider.properties.getValue(propertyName);
        const seedValue = toSixDigitHex(
            typeof resolved === 'string' ? resolved : declBefore.value,
        );
        const seed = await commitVariants(
            provider.owner, provider.properties, propertyName,
            [{ name: 'Variant 1', value: seedValue }],
            { record: false, transition: seedTransition },
        );
        if (seed.errors.length > 0 || !seed.command) {
            await rollback();
            return { errors: seed.errors, propertyName: null, command: null };
        }
        commands.push(seed.command);
    } else if (seedTransition) {
        // Adopting an existing variant set that lacks a transition — seed it.
        const seed = await commitVariants(
            provider.owner, provider.properties, propertyName,
            declBefore.variants,
            { record: false, transition: seedTransition },
        );
        if (seed.errors.length > 0 || !seed.command) {
            await rollback();
            return { errors: seed.errors, propertyName: null, command: null };
        }
        commands.push(seed.command);
    }

    // 3. One undo entry for the whole action (bind + seed, or just seed).
    let command: UndoCommand | null = null;
    if (commands.length === 1) {
        command = commands[0];
    } else if (commands.length > 1) {
        command = new CompositeCommand(`Create variant set on ${sceneObject.name}`, commands);
    }
    if (command && record && !getUndoManager().isUndoRedoing) {
        getUndoManager().push(command);
    }
    if (command) {
        dispatchBehaviorChanged({
            objectPath: provider.owner.getPath(),
            behaviorType: 'Properties',
            action: 'updated',
        });
    }
    return { errors: [], propertyName, command };
}

/**
 * Find a color declaration on `obj` or an ancestor that a prior variant set for
 * `materialName` would have created — matched by `label` (the pretty material
 * name) or by the un-deduped sanitized name. Prefer a variants-bearing match so
 * "Restore Default" → re-add re-attaches the SAME set rather than orphaning it.
 * Returns the nearest such declaration (self before ancestors, variants before
 * variant-less), or null.
 */
function findReusableColorDeclaration(
    obj: SceneObject,
    materialName: string,
): { owner: SceneObject; name: string } | null {
    const wantName = sanitizePropertyName(materialName, new Set());
    let cur: SceneObject | null = obj;
    let variantLessFallback: { owner: SceneObject; name: string } | null = null;
    while (cur) {
        const props = cur.getBehavior(Properties);
        for (const d of props?.getData().properties ?? []) {
            if (d.type !== 'color') continue;
            const matches = d.label === materialName || d.name === wantName;
            if (!matches) continue;
            if (d.variants && d.variants.length > 0) return { owner: cur, name: d.name };
            variantLessFallback ??= { owner: cur, name: d.name };
        }
        cur = cur.getParent();
    }
    return variantLessFallback;
}

/** All declaration names on `obj` and its ancestors — dedup pool for sanitize. */
function collectDeclaredNames(obj: SceneObject): Set<string> {
    const names = new Set<string>();
    let cur: SceneObject | null = obj;
    while (cur) {
        const props = cur.getBehavior(Properties);
        if (props) for (const d of props.getData().properties ?? []) names.add(d.name);
        cur = cur.getParent();
    }
    return names;
}

export interface VariantOptionResult {
    errors: ValidationError[];
    command: UndoCommand | null;
}

/**
 * Commit a new `variants` array onto the color declaration named `propertyName`
 * on `owner`'s Properties behavior: snapshot getData(), swap the declaration
 * immutably, commit, wrap in a BehaviorCommitCommand, push (unless record:false),
 * and dispatch a Properties 'updated' event on the OWNER's path.
 */
async function commitVariants(
    owner: SceneObject,
    properties: Properties,
    propertyName: string,
    variants: { name: string; value: string }[],
    opts: { record?: boolean; fieldKey?: string; transition?: TimeTransition } = {},
): Promise<VariantOptionResult> {
    const before = properties.getData();
    const decl = (before.properties ?? []).find(d => d.name === propertyName);
    if (!decl || decl.type !== 'color') {
        return {
            errors: [{ message: `Property "${propertyName}" is not a color declaration`, code: 'NOT_COLOR' }],
            command: null,
        };
    }
    const declarations: PropertyDeclaration[] = (before.properties ?? []).map(d =>
        d.name === propertyName && d.type === 'color'
            ? { ...d, variants, ...(opts.transition !== undefined ? { transition: opts.transition } : {}) }
            : d,
    );
    const errors = await properties.commit({ properties: declarations });
    if (errors.length > 0) return { errors, command: null };

    // fieldKey lets rapid recolor drags coalesce into one undo entry (the color
    // picker streams events); null for structural edits (add/rename/remove).
    const command = new BehaviorCommitCommand(
        owner, 'Properties', before, properties.getData(), opts.fieldKey ?? null,
    );
    if (opts.record !== false && !getUndoManager().isUndoRedoing) {
        getUndoManager().push(command);
    }
    dispatchBehaviorChanged({
        objectPath: owner.getPath(), behaviorType: 'Properties', action: 'updated',
    });
    return { errors: [], command };
}

/** Locate the variant array for `propertyName`, or an error result. */
function resolveVariants(
    sceneObject: SceneObject,
    propertyName: string,
): { owner: SceneObject; properties: Properties; variants: { name: string; value: string }[] } | VariantOptionResult {
    const provider = findPropertyProvider(sceneObject, propertyName);
    if (!provider) {
        return {
            errors: [{ message: `Bound property "${propertyName}" has no providing Properties behavior`, code: 'NO_PROVIDER' }],
            command: null,
        };
    }
    const decl = (provider.properties.getData().properties ?? []).find(d => d.name === propertyName);
    if (!decl || decl.type !== 'color') {
        return {
            errors: [{ message: `Property "${propertyName}" is not a color declaration`, code: 'NOT_COLOR' }],
            command: null,
        };
    }
    return { owner: provider.owner, properties: provider.properties, variants: decl.variants ?? [] };
}

/** Dedupe a display name within a variant set (`Variant 2`, `Variant 3`…). */
function dedupeVariantName(name: string, existing: { name: string }[]): string {
    const taken = new Set(existing.map(v => v.name));
    if (!taken.has(name)) return name;
    let i = 2;
    while (taken.has(`${name}${i}`)) i++;
    return `${name}${i}`;
}

/** Recolor the variant at `index` in place (its value, not its name). */
export async function updateVariantOption(
    sceneObject: SceneObject,
    propertyName: string,
    index: number,
    value: string,
    opts: { record?: boolean } = {},
): Promise<VariantOptionResult> {
    const resolved = resolveVariants(sceneObject, propertyName);
    if ('errors' in resolved) return resolved;
    const { owner, properties, variants } = resolved;
    if (index < 0 || index >= variants.length) {
        return { errors: [{ message: `Variant index ${index} out of range`, code: 'BAD_INDEX' }], command: null };
    }

    const hex = toSixDigitHex(value);
    if (variants[index].value.toLowerCase() === hex.toLowerCase()) {
        return { errors: [], command: null }; // no-op — same color
    }
    const next = variants.map((v, i) => i === index ? { ...v, value: hex } : v);
    return commitVariants(owner, properties, propertyName, next, {
        ...opts,
        fieldKey: `variant:${propertyName}[${index}].value`,
    });
}

/** Append a variant option (auto-named `Variant N` when `name` is omitted). */
export async function addVariantOption(
    sceneObject: SceneObject,
    propertyName: string,
    value: string,
    name?: string,
    opts: { record?: boolean } = {},
): Promise<VariantOptionResult> {
    const resolved = resolveVariants(sceneObject, propertyName);
    if ('errors' in resolved) return resolved;
    const { owner, properties, variants } = resolved;

    const displayName = dedupeVariantName(name ?? `Variant ${variants.length + 1}`, variants);
    const next = [...variants, { name: displayName, value: toSixDigitHex(value) }];
    return commitVariants(owner, properties, propertyName, next, opts);
}

/** Rename the variant at `index`. */
export async function renameVariantOption(
    sceneObject: SceneObject,
    propertyName: string,
    index: number,
    newName: string,
    opts: { record?: boolean } = {},
): Promise<VariantOptionResult> {
    const resolved = resolveVariants(sceneObject, propertyName);
    if ('errors' in resolved) return resolved;
    const { owner, properties, variants } = resolved;
    if (index < 0 || index >= variants.length) {
        return { errors: [{ message: `Variant index ${index} out of range`, code: 'BAD_INDEX' }], command: null };
    }

    const deduped = dedupeVariantName(newName, variants.filter((_, i) => i !== index));
    const next = variants.map((v, i) => i === index ? { ...v, name: deduped } : v);
    return commitVariants(owner, properties, propertyName, next, opts);
}

/** Remove the variant at `index`. */
export async function removeVariantOption(
    sceneObject: SceneObject,
    propertyName: string,
    index: number,
    opts: { record?: boolean } = {},
): Promise<VariantOptionResult> {
    const resolved = resolveVariants(sceneObject, propertyName);
    if ('errors' in resolved) return resolved;
    const { owner, properties, variants } = resolved;
    if (index < 0 || index >= variants.length) {
        return { errors: [{ message: `Variant index ${index} out of range`, code: 'BAD_INDEX' }], command: null };
    }

    const next = variants.filter((_, i) => i !== index);
    return commitVariants(owner, properties, propertyName, next, opts);
}

/**
 * Set (or clear) the default `transition` on a variant set's color declaration.
 * `undefined` deletes the field (switches snap). One `BehaviorCommitCommand`, an
 * undo entry unless `record: false`, and a Properties 'updated' dispatch — the
 * same shape as the sibling option helpers.
 */
export async function setVariantSetTransition(
    sceneObject: SceneObject,
    propertyName: string,
    transition: TimeTransition | undefined,
    opts: { record?: boolean } = {},
): Promise<VariantOptionResult> {
    const provider = findPropertyProvider(sceneObject, propertyName);
    if (!provider) {
        return {
            errors: [{ message: `Bound property "${propertyName}" has no providing Properties behavior`, code: 'NO_PROVIDER' }],
            command: null,
        };
    }
    const { owner, properties } = provider;
    const before = properties.getData();
    const decl = (before.properties ?? []).find(d => d.name === propertyName);
    if (!decl || decl.type !== 'color') {
        return {
            errors: [{ message: `Property "${propertyName}" is not a color declaration`, code: 'NOT_COLOR' }],
            command: null,
        };
    }

    const declarations: PropertyDeclaration[] = (before.properties ?? []).map(d => {
        if (d.name !== propertyName || d.type !== 'color') return d;
        // Immutably rebuild without the transition field, then re-add if setting.
        const { transition: _drop, ...rest } = d;
        return transition !== undefined ? { ...rest, transition } : rest;
    });

    const errors = await properties.commit({ properties: declarations });
    if (errors.length > 0) return { errors, command: null };

    const command = new BehaviorCommitCommand(
        owner, 'Properties', before, properties.getData(), null,
    );
    if (opts.record !== false && !getUndoManager().isUndoRedoing) {
        getUndoManager().push(command);
    }
    dispatchBehaviorChanged({
        objectPath: owner.getPath(), behaviorType: 'Properties', action: 'updated',
    });
    return { errors: [], command };
}
